<template>

  <div class="d-flex flex-column" style="min-width:95vw; max-width:95vw">
    <v-sheet class="ma-1">
      <v-card
        variant="outlined"
        title="OBSFDC Records"
        flat>
        <template v-slot:text>
          <div class="d-flex align-center" style="gap: 8px;">
            <v-text-field v-model="search" label="Filter" prepend-inner-icon="mdi-filter-outline"
              variant="outlined" single-line hide-details aria-label="Filter OBSFDC records"></v-text-field>
            <v-btn prepend-icon="mdi-download" @click="downloadFile" @keydown.enter="downloadFile"
              @keydown.space.prevent="downloadFile" variant="outlined" :disabled="filteredRecords.length === 0"
              tabindex="0" aria-label="Download filtered OBSFDC records as CSV">Download</v-btn>
          </div>
        </template>

        <v-data-table
          :items-per-page="25"
          :items-per-page-options="[10, 25, 50, -1]"
          :headers="headers"
          :items="obsfdcRecords"
          :search="search"
          item-key="id"
          role="table"
          aria-label="OBSFDC records data">

          <template v-slot:item.edit="{ item }">
            <v-btn icon="mdi-pencil" @click="edit(item)" variant="text" aria-label="Edit OBSFDC record" />
          </template>
        </v-data-table>
      </v-card>
    </v-sheet>
  </div>

  <div>
    <Transition name="modal">
      <div v-if="editShow" class="modal-mask">
        <div class="modal-wrapper">
          <div class="modal-container">
            <div class="modal-body">
              <v-form ref="editForm" @submit.prevent="saveObsfdcRecord">
                <div class="d-flex d-flex-row pt-5">
                  <h3 style="width:80%">Edit OBSFDC Record</h3>
                  <v-btn icon="mdi-check-bold" type="submit" class="modal-save-button" size="x-small" variant="text" aria-label="Save OBSFDC record">
                    <v-icon>mdi-check-bold</v-icon>
                    <v-tooltip activator="parent" location="top">Save and Close</v-tooltip>
                  </v-btn>
                  <v-btn icon="mdi-close-thick" class="modal-cancel-button" @click="close" size="x-small" variant="text" aria-label="Cancel edit">
                    <v-icon>mdi-close-thick</v-icon>
                    <v-tooltip activator="parent" location="top">Cancel and Close</v-tooltip>
                  </v-btn>
                </div>

                <v-text-field label="Account Name" v-model="editRecord.accountName" density="comfortable" readonly></v-text-field>
                <v-text-field label="Affinity Account Id" v-model="editRecord.affinityAccountId" density="comfortable" readonly></v-text-field>
                <v-text-field label="Country" v-model="editRecord.partnerRegistrationCountry" density="comfortable" readonly></v-text-field>

                <div class="d-flex align-center" style="gap: 4px;">
                  <v-text-field
                    label="Email Local Part (Username)"
                    :model-value="emailLocalPart"
                    density="comfortable"
                    readonly
                    hide-details
                    style="max-width: 180px; flex-shrink: 0;"
                    aria-label="Email local part (read-only)"
                  ></v-text-field>
                  <v-text-field
                    v-model="editEmailDomain"
                    label="Domain"
                    density="comfortable"
                    hide-details="auto"
                    :rules="[
                      v => !!v || 'Domain is required',
                      v => !v.includes('@') || 'Domain must not contain @',
                      v => /^[^.\s]+(\.[^.\s]+)*\.[a-zA-Z]{2,}$/.test(v) || 'Domain must include a valid top-level domain (e.g. email.com or email.com.uk)'
                    ]"
                    aria-label="Edit email domain"
                    style="flex: 1;"
                  ></v-text-field>
                </div>
              </v-form>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useSystemStore } from '@/stores/system'

const systemStore = useSystemStore()

const obsfdcRecords = ref([])
const search = ref('')

const editForm = ref(null)
const editShow = ref(false)
const editRecord = ref({})
const editEmailDomain = ref('')

const headers = [
  { key: 'affinityAccountId', title: 'Affinity Account Id' },
  { key: 'accountName', title: 'Account Name' },
  { key: 'partnerRegistrationCountry', title: 'Country' },
  { key: 'partnerRegistrationEmail', title: 'Email' },
  { key: 'quarter', title: 'Quarter' },
  { key: 'week', title: 'Week' },
  { key: 'importDate', title: 'Import Date' },
  { key: 'edit', title: 'Edit', sortable: false }
]

const filteredRecords = computed(() => {
  const term = search.value?.toString().trim().toLowerCase()
  if (!term) return obsfdcRecords.value
  return obsfdcRecords.value.filter(record =>
    headers.some(header => {
      if (header.key === 'edit') return false
      const value = record[header.key]
      return value != null && value.toString().toLowerCase().includes(term)
    })
  )
})

const emailLocalPart = computed(() => {
  const email = editRecord.value.partnerRegistrationEmail
  if (email && email.includes('@')) {
    return email.substring(0, email.indexOf('@') + 1)
  }
  return '@'
})

onMounted(() => {
  loadRecords()
})

const loadRecords = () => {
  systemStore.getObsfdcRecords().then(result => {
    if (result.success) {
      obsfdcRecords.value = result.obsfdcRecords
    }
  }).catch(error => {
    console.log('Get OBSFDC Records', error)
  })
}

const downloadFile = () => {
  if (filteredRecords.value.length === 0) return

  const data = filteredRecords.value.map(record => ({
    'Affinity Account Id': record.affinityAccountId,
    'Account Name': record.accountName,
    'Country': record.partnerRegistrationCountry,
    'Email': record.partnerRegistrationEmail,
    'Quarter': record.quarter,
    'Week': record.week,
    'Import Date': record.importDate
  }))

  const now = new Date()
  const day = String(now.getDate()).padStart(2, '0')
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const year = now.getFullYear()
  const fileName = `OBSFDC_Records_Download_${day}${month}${year}.csv`

  systemStore.downloadCsvFile(data, fileName)
}

const edit = (item) => {
  editRecord.value = { ...item }
  const email = item.partnerRegistrationEmail
  if (email && email.includes('@')) {
    editEmailDomain.value = email.substring(email.indexOf('@') + 1)
  } else {
    editEmailDomain.value = ''
  }
  editShow.value = true
}

const saveObsfdcRecord = async () => {
  const { valid } = await editForm.value.validate()
  if (!valid) return

  systemStore.updateObsfdcEmail(editRecord.value.id, editEmailDomain.value).then(result => {
    if (result.success === true) {
      close()
    }
  }).catch(error => {
    console.log('Update OBSFDC Email', error)
  })
}

const close = () => {
  editShow.value = false
  editForm.value?.reset()
  loadRecords()
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 500;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 500px;
  margin: 5px auto;
  padding: 5px 30px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
}

.modal-body {
  margin: 20px 0;
}

.modal-save-button {
  margin: 0px 10px;
}

.modal-cancel-button {
  margin: 0px 10px;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
